'use client';

import type { CheckboxProps } from '@magicschool/spellbook/components/Checkbox';
import { Checkbox } from '@magicschool/spellbook/components/Checkbox';
import { useId } from '@magicschool/spellbook/hooks/useId';
import { cn } from '@magicschool/spellbook/lib/utils';
import type { VariantProps } from 'class-variance-authority';
import { cva } from 'class-variance-authority';
import { useState } from 'react';

export type CheckboxGroupOption = CheckboxProps & {
  value: string;
  label: string;
};

export type CheckboxGroupProps = React.ComponentProps<'div'> &
  VariantProps<typeof checkboxGroupVariants> & {
    /** The list of checkbox options to render. Each option maps to an individual `Checkbox` with `value`, `label`, and optional `description` or `disabled`. */
    options: CheckboxGroupOption[];
    /** Controlled selected values. An array of option `value` strings that are currently checked. */
    value?: string[];
    /** Initial selected values for uncontrolled usage. An array of option `value` strings that are checked on mount. */
    defaultValue?: string[];
    /** Callback fired when any checkbox in the group changes. Receives the updated array of selected `value` strings. */
    onCheckedChange?: (value: string[]) => void;
    /** Disables all checkboxes in the group. Individual options can also set their own `disabled`. */
    disabled?: boolean;
    /** Marks the group as required for form validation. */
    required?: boolean;
    /** Marks all checkboxes in the group as invalid and sets `aria-invalid="true"`. Use with form validation. */
    invalid?: boolean;
    /** The shared `name` attribute for all checkboxes in the group, used for form submission. Auto-generated if not provided. */
    name?: string;
    /** The `id` of an external label element to associate with the group via `aria-labelledby`. */
    labelId?: string;
  };

const checkboxGroupVariants = cva('flex gap-2 w-full', {
  variants: {
    orientation: {
      horizontal: 'flex-row flex-wrap row-gap-4',
      vertical: 'flex-col',
    },
  },
});

/**
 * Groups multiple checkboxes under a shared name with managed multi-select state. Renders individual `Checkbox` components from an `options` array.
 *
 * ### When to use
 * - Selecting multiple options from a short, visible list (e.g., alert preferences, agreement checkboxes)
 * - When all options should be visible at once without a dropdown
 * - When options need descriptions or individually disabled states
 *
 * ### When NOT to use
 * - For selecting from a long list with search and compact display: use `MultiSelect` instead
 * - For a single boolean toggle: use `Checkbox` directly
 * - For mutually exclusive selection (pick one): use `RadioGroup` instead
 *
 * ### API
 * - `options`: Array of `CheckboxGroupOption` objects, each rendered as a `Checkbox`. Supports per-option `label`, `value`, `description`, and `disabled`.
 * - `value` / `defaultValue`: Controlled or uncontrolled selected values as an array of `value` strings.
 * - `onCheckedChange`: Callback with the updated array of selected `value` strings.
 * - `orientation`: Layout direction: `'vertical'` (default) stacks checkboxes, `'horizontal'` arranges them in a wrapping row.
 * - `disabled`: Disables all checkboxes. Individual options can also be disabled independently.
 * - `invalid`: Sets `aria-invalid="true"` on all checkboxes and applies error border styling.
 * - `labelId`: Links an external label to the group via `aria-labelledby`.
 *
 * ### Visual states
 * - **Disabled**: All checkboxes render as muted and non-interactive when group-level `disabled` is set
 * - **Invalid**: All checkboxes show error border styling when group-level `invalid` is set
 *
 * ### Accessibility
 * - Renders as a `<div role="group">` to semantically group the checkboxes
 * - Use `labelId` to link an external label via `aria-labelledby` — without it, the group has no accessible name
 * - All checkboxes share the same `name` attribute for submission
 *
 * ### CheckboxGroup vs. SwitchGroup
 * Both group multiple toggleable options, but `CheckboxGroup` is for deferred multi-select (choices confirmed by a later action),
 * while `SwitchGroup` is for immediate independent toggles. `CheckboxGroup` represents a single conceptual field — all items share
 * one `name` and produce an array of selected values (e.g., "which toppings do you want?"). `SwitchGroup` is primarily organizational —
 * it groups independent switches under a shared layout and name for convenience, but each toggle is conceptually standalone
 * (e.g., "configure your notification preferences").
 *
 * See the [Checkbox documentation](?path=/docs/components-checkbox--docs) for a full comparison table of all selection components.
 */
export const CheckboxGroup: React.FC<CheckboxGroupProps> = ({
  options = [],
  value,
  defaultValue,
  labelId,
  disabled = false,
  invalid = false,
  name: groupName,
  className,
  onCheckedChange,
  orientation = 'vertical',
  ...props
}) => {
  const id = useId(groupName);
  const name = groupName || id;
  const isControlled = value !== undefined;
  const [internalValue, setInternalValue] = useState<string[]>(defaultValue || []);
  const selectedValues = isControlled ? value : internalValue;

  const handleOnCheckedChange = (checked: boolean, option: CheckboxGroupOption) => {
    const newValue = checked ? [...selectedValues, option.value] : selectedValues.filter((v) => v !== option.value);
    if (!isControlled) {
      setInternalValue(newValue);
    }
    onCheckedChange?.(newValue);
  };

  return (
    <div
      role="group"
      data-spellbook-name="checkbox-group"
      className={cn(checkboxGroupVariants({ orientation }), className)}
      id={id}
      {...(labelId && { 'aria-labelledby': labelId })}
      {...props}
    >
      {options.map((option, index) => {
        const checkboxId = `${id}-${index}`;
        const handleOptionCheckedChange = (checked: boolean) => {
          handleOnCheckedChange(checked, option);
        };

        return (
          <Checkbox
            key={checkboxId}
            id={checkboxId}
            name={name}
            value={option.value}
            label={option.label}
            description={option.description}
            disabled={disabled || option.disabled}
            invalid={invalid}
            checked={selectedValues.includes(option.value)}
            onCheckedChange={handleOptionCheckedChange}
          />
        );
      })}
    </div>
  );
};
